/**
 * Relay Environment Configuration
 *
 * This module provides the Relay environment for GraphQL operations.
 */

import {
  Environment,
  Network,
  RecordSource,
  Store,
  FetchFunction,
  GraphQLResponse,
} from 'relay-runtime';
import { getCredentials } from '../api/http';
import { getGraphQLEndpoint } from '../api/graphql/endpoints';

/**
 * Removes null values from an object (shallow)
 * GraphQL servers may reject explicit null values for optional parameters
 */
function stripNullValues(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    if (obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Fetch function for Relay Network
 * Handles authentication and request/response formatting
 */
const fetchRelay: FetchFunction = async (request, variables) => {
  const { graphqlToken } = getCredentials();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (graphqlToken) {
    headers['Authorization'] = `Bearer ${graphqlToken}`;
  }

  // Strip null values from variables to avoid GraphQL server rejection
  const cleanedVariables = stripNullValues(variables);

  const response = await fetch(getGraphQLEndpoint(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: request.text,
      variables: cleanedVariables,
    }),
  });

  const json: GraphQLResponse = await response.json();

  // Handle 401 Unauthorized
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  }

  return json;
};

/**
 * Create a new Relay Environment instance
 */
function createRelayEnvironment(): Environment {
  const network = Network.create(fetchRelay);
  const store = new Store(new RecordSource());

  return new Environment({
    network,
    store,
  });
}

/**
 * Singleton Relay Environment for the application
 * Use this for most cases unless you need a fresh environment
 */
export const relayEnvironment = createRelayEnvironment();

/**
 * Factory function to create a new environment
 * Useful for testing or server-side rendering
 */
export { createRelayEnvironment };

export default relayEnvironment;
